import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Table, Badge, ProgressBar, Modal } from 'react-bootstrap';
import api from '../../services/api';
import { showToast } from '../../hooks/useToast';

const Categories = () => {
  const [stats, setStats] = useState({
    SchoolYear: { total: 0, synced: 0 },
    LevelType: { total: 0, synced: 0 },
    Section: { total: 0, synced: 0 }
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState({ show: false, current: 0, total: 0, type: '' });
  const [showComparison, setShowComparison] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [syncingUpdates, setSyncingUpdates] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.get('/categories/stats');
      setStats(response.data);
    } catch (error) {
      showToast('Error cargando estadísticas', 'danger');
    }
  };

  const migrateModel = async (model) => {
    if (isProcessing) return;
    setIsProcessing(true);
    
    const modelNames = {
      SchoolYear: 'años escolares',
      LevelType: 'tipos de nivel', 
      Section: 'secciones'
    };
    
    const modelText = modelNames[model];
    const totalItems = stats[model]?.total - stats[model]?.synced || 0;
    
    setMigrationProgress({ show: true, current: 0, total: totalItems, type: modelText });
    
    try {
      let hasMore = true;
      let totalProcessed = 0;
      
      while (hasMore) {
        const response = await api.post(`/categories/migrate/${model}`, { batchSize: 5 });
        
        totalProcessed += response.data.processed;
        hasMore = response.data.hasMore;
        
        setMigrationProgress(prev => ({ ...prev, current: totalProcessed }));
        
        if (response.data.processed === 0) break;
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      showToast(`${modelText} migrados: ${totalProcessed}`, 'success');
      await loadStats();
    } catch (error) {
      showToast(`Error migrando ${modelText}`, 'danger');
    } finally {
      setIsProcessing(false);
      setMigrationProgress({ show: false, current: 0, total: 0, type: '' });
    }
  };

  const compareWithMoodle = async () => {
    setLoadingComparison(true);
    try {
      const response = await api.get('/categories/compare');
      setComparison(response.data);
      setShowComparison(true);
      if (response.data.updated > 0) {
        showToast(`${response.data.updated} categorías actualizadas`, 'success');
        await loadStats();
      }
    } catch (error) {
      showToast('Error comparando con Moodle', 'danger');
    } finally {
      setLoadingComparison(false);
    }
  };

  const syncUpdatesFromMoodle = async () => {
    setSyncingUpdates(true);
    try {
      const response = await api.post('/categories/sync-updates');
      if (response.data.updated > 0) {
        showToast(response.data.message, 'success');
        await loadStats();
      } else {
        showToast(response.data.message, 'info');
      }
    } catch (error) {
      showToast('Error sincronizando actualizaciones', 'danger');
    } finally {
      setSyncingUpdates(false);
    }
  };

  const getProgressPercentage = (synced, total) => total > 0 ? Math.round((synced / total) * 100) : 0;

  const StatCard = ({ title, stats, color, model, icon, description }) => {
    const percentage = getProgressPercentage(stats.synced, stats.total);
    const pending = stats.total - stats.synced;
    
    return (
      <Card className="h-100 border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 className="mb-1">{title}</h6>
              <small className="text-muted">{description}</small>
            </div>
            <div className={`bg-${color} text-white rounded-circle d-flex align-items-center justify-content-center`} style={{width: '50px', height: '50px'}}>
              <i className={`bi ${icon} fs-5`}></i>
            </div>
          </div>
          
          <div className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <small className="text-muted">Progreso</small>
              <small className={`fw-bold text-${color}`}>{percentage}%</small>
            </div>
            <ProgressBar now={percentage} variant={color} className="mb-2" style={{height: '8px'}} />
          </div>
          
          <Row className="text-center mb-3">
            <Col>
              <div className={`h5 text-${color} mb-0`}>{stats.synced}</div>
              <small className="text-muted">Migrados</small>
            </Col>
            <Col>
              <div className="h5 text-warning mb-0">{pending}</div>
              <small className="text-muted">Pendientes</small>
            </Col>
            <Col>
              <div className="h5 text-secondary mb-0">{stats.total}</div>
              <small className="text-muted">Total</small>
            </Col>
          </Row>
          
          <Button 
            variant={pending > 0 ? color : 'outline-secondary'}
            size="sm" 
            className="w-100"
            onClick={() => migrateModel(model)}
            disabled={isProcessing || pending === 0}
          >
            <i className="bi bi-cloud-upload me-1"></i>
            {pending === 0 ? 'Completado' : isProcessing ? 'Migrando...' : `Migrar ${pending}`}
          </Button>
        </Card.Body>
      </Card>
    );
  };

  const totalCategories = Object.values(stats).reduce((sum, stat) => sum + stat.total, 0);
  const syncedCategories = Object.values(stats).reduce((sum, stat) => sum + stat.synced, 0);
  const pendingCategories = totalCategories - syncedCategories;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Migración de Categorías</h2>
        <div className="d-flex gap-2">
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={compareWithMoodle}
            disabled={loadingComparison}
            className="me-2"
          >
            <i className="bi bi-arrow-left-right me-1"></i>
            {loadingComparison ? 'Comparando...' : 'Comparar con Moodle'}
          </Button>
          <Button 
            variant="outline-success" 
            size="sm" 
            onClick={syncUpdatesFromMoodle}
            disabled={syncingUpdates}
          >
            <i className="bi bi-cloud-download me-1"></i>
            {syncingUpdates ? 'Sincronizando...' : 'Sincronizar Cambios'}
          </Button>
          <Badge bg="success">{syncedCategories} Migradas</Badge>
          <Badge bg="warning">{pendingCategories} Pendientes</Badge>
        </div>
      </div>

      <Row className="g-4 mb-4">
        <Col md={4}>
          <StatCard
            title="Años Escolares"
            description="Períodos académicos"
            stats={stats.SchoolYear}
            color="primary"
            model="SchoolYear"
            icon="bi-calendar-event"
          />
        </Col>
        <Col md={4}>
          <StatCard
            title="Tipos de Nivel"
            description="Niveles educativos"
            stats={stats.LevelType}
            color="info"
            model="LevelType"
            icon="bi-diagram-2"
          />
        </Col>
        <Col md={4}>
          <StatCard
            title="Secciones"
            description="Grupos de estudiantes"
            stats={stats.Section}
            color="success"
            model="Section"
            icon="bi-collection"
          />
        </Col>
      </Row>

      {pendingCategories === 0 && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <i className="bi bi-check-circle text-success" style={{fontSize: '3rem'}}></i>
            <h5 className="mt-3 text-success">¡Todas las categorías están migradas!</h5>
            <p className="text-muted">El sistema de categorías está completamente sincronizado con Moodle</p>
          </Card.Body>
        </Card>
      )}

      {pendingCategories > 0 && (
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-light">
            <h6 className="mb-0">
              <i className="bi bi-info-circle text-info me-2"></i>
              Orden de Migración
            </h6>
          </Card.Header>
          <Card.Body>
            <div className="d-flex align-items-center justify-content-between p-3 border rounded mb-2">
              <div className="d-flex align-items-center">
                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '30px', height: '30px'}}>
                  <span className="small fw-bold">1</span>
                </div>
                <div>
                  <div className="fw-medium">Años Escolares</div>
                  <small className="text-muted">Migrar primero como categorías padre</small>
                </div>
              </div>
              <Badge bg={stats.SchoolYear.total - stats.SchoolYear.synced === 0 ? 'success' : 'warning'}>
                {stats.SchoolYear.total - stats.SchoolYear.synced === 0 ? 'Completado' : `${stats.SchoolYear.total - stats.SchoolYear.synced} pendientes`}
              </Badge>
            </div>
            
            <div className="d-flex align-items-center justify-content-between p-3 border rounded mb-2">
              <div className="d-flex align-items-center">
                <div className="bg-info text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '30px', height: '30px'}}>
                  <span className="small fw-bold">2</span>
                </div>
                <div>
                  <div className="fw-medium">Tipos de Nivel</div>
                  <small className="text-muted">Requiere años escolares migrados</small>
                </div>
              </div>
              <Badge bg={stats.LevelType.total - stats.LevelType.synced === 0 ? 'success' : 'warning'}>
                {stats.LevelType.total - stats.LevelType.synced === 0 ? 'Completado' : `${stats.LevelType.total - stats.LevelType.synced} pendientes`}
              </Badge>
            </div>
            
            <div className="d-flex align-items-center justify-content-between p-3 border rounded">
              <div className="d-flex align-items-center">
                <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '30px', height: '30px'}}>
                  <span className="small fw-bold">3</span>
                </div>
                <div>
                  <div className="fw-medium">Secciones</div>
                  <small className="text-muted">Requiere tipos de nivel migrados</small>
                </div>
              </div>
              <Badge bg={stats.Section.total - stats.Section.synced === 0 ? 'success' : 'warning'}>
                {stats.Section.total - stats.Section.synced === 0 ? 'Completado' : `${stats.Section.total - stats.Section.synced} pendientes`}
              </Badge>
            </div>
          </Card.Body>
        </Card>
      )}

      <Modal show={migrationProgress.show} backdrop="static" keyboard={false} centered>
        <Modal.Body className="text-center py-4">
          <div className="mb-3">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Migrando...</span>
            </div>
            <h5>Migrando {migrationProgress.type}</h5>
            <p className="text-muted mb-0">
              {migrationProgress.current} de {migrationProgress.total} elementos procesados
            </p>
          </div>
          
          <ProgressBar 
            now={migrationProgress.total > 0 ? (migrationProgress.current / migrationProgress.total) * 100 : 0}
            variant="primary"
            className="mb-3"
            style={{height: '10px'}}
          />
          
          <div className="text-muted">
            {migrationProgress.total > 0 ? Math.round((migrationProgress.current / migrationProgress.total) * 100) : 0}% completado
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showComparison} onHide={() => setShowComparison(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Comparación con Moodle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {comparison && (
            <div>
              <Row className="mb-4">
                <Col md={6}>
                  <Card className="border-primary">
                    <Card.Header className="bg-primary text-white">
                      <h6 className="mb-0">Sistema Local</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Total:</span>
                        <Badge bg="primary">{comparison.local.total}</Badge>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Años Escolares:</span>
                        <Badge bg="info">{comparison.local.schoolYears}</Badge>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Tipos de Nivel:</span>
                        <Badge bg="info">{comparison.local.levelTypes}</Badge>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Secciones:</span>
                        <Badge bg="info">{comparison.local.sections}</Badge>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="border-success">
                    <Card.Header className="bg-success text-white">
                      <h6 className="mb-0">Moodle</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Total:</span>
                        <Badge bg="success">{comparison.moodle.total}</Badge>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Coincidencias:</span>
                        <Badge bg="success">{comparison.matches.length}</Badge>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Solo en Local:</span>
                        <Badge bg="warning">{comparison.localOnly.length}</Badge>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Solo en Moodle:</span>
                        <Badge bg="info">{comparison.moodleOnly.length}</Badge>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {comparison.localOnly.length > 0 && (
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">Categorías pendientes de migrar ({comparison.localOnly.length})</h6>
                  </Card.Header>
                  <Card.Body>
                    <Table striped size="sm">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th>Nombre</th>
                          <th>ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparison.localOnly.slice(0, 10).map((item, index) => (
                          <tr key={index}>
                            <td><Badge bg="secondary">{item.type}</Badge></td>
                            <td>{item.name}</td>
                            <td>{item.id}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {comparison.localOnly.length > 10 && (
                      <small className="text-muted">Mostrando 10 de {comparison.localOnly.length} elementos</small>
                    )}
                  </Card.Body>
                </Card>
              )}

              {comparison.matches.length > 0 && (
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Categorías sincronizadas ({comparison.matches.length})</h6>
                  </Card.Header>
                  <Card.Body>
                    <Table striped size="sm">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th>Nombre Local</th>
                          <th>Nombre Moodle</th>
                          <th>ID Moodle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparison.matches.slice(0, 10).map((match, index) => (
                          <tr key={index}>
                            <td><Badge bg="secondary">{match.local.type}</Badge></td>
                            <td>{match.local.name}</td>
                            <td>{match.moodle.name}</td>
                            <td>{match.moodle.id}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {comparison.matches.length > 10 && (
                      <small className="text-muted">Mostrando 10 de {comparison.matches.length} elementos</small>
                    )}
                  </Card.Body>
                </Card>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowComparison(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Categories;